(() => {
  if (customElements.get('clairatier-delivery-estimate')) return;

  class ClairatierDeliveryEstimate extends HTMLElement {
    static parseDate(value) {
      if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
      const [year, month, day] = value.split('-').map(Number);
      if (year < 1900 || year > 9999) return null;
      const date = new Date(Date.UTC(year, month - 1, day));
      return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? date : null;
    }

    static iso(date) {
      return date.toISOString().slice(0, 10);
    }

    static today(now, timeZone) {
      const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
      const value = (name) => parts.find((part) => part.type === name).value;
      return this.parseDate(`${value('year')}-${value('month')}-${value('day')}`);
    }

    static addDays(date, count, business = false, closures = []) {
      const result = new Date(date.getTime());
      let remaining = count;
      while (remaining > 0) {
        result.setUTCDate(result.getUTCDate() + 1);
        if (!business || (result.getUTCDay() > 0 && result.getUTCDay() < 6 && !closures.includes(this.iso(result)))) remaining -= 1;
      }
      return result;
    }

    static config(input) {
      if (!input || input.confirmed !== true || input.available !== true || !['standard', 'personalized'].includes(input.mode)) return null;
      if (typeof input.timeZone !== 'string' || typeof input.locale !== 'string') return null;
      try {
        new Intl.DateTimeFormat(input.locale, { timeZone: input.timeZone }).format();
      } catch (_) {
        return null;
      }
      if (typeof input.closures !== 'string') return null;
      const closures = input.closures.trim().split(/[,\s]+/).filter(Boolean);
      if (closures.some((date) => !this.parseDate(date))) return null;
      const range = (minimum, maximum) => Number.isInteger(minimum) && Number.isInteger(maximum) && minimum >= 1 && maximum >= minimum && maximum <= 90;
      if (input.mode === 'standard' && !range(input.processingMin, input.processingMax)) return null;
      if (input.transitConfirmed === true && !range(input.transitMin, input.transitMax)) return null;
      return { ...input, closures };
    }

    static estimate(input, now = new Date(), destination = '') {
      const config = this.config(input);
      if (!config || !Number.isFinite(now.getTime())) return null;
      const order = this.today(now, config.timeZone);
      if (!order) return null;
      const personalized = config.mode === 'personalized';
      const processingEnd = personalized ? this.addDays(order, 14) : null;
      const dispatchStart = personalized ? processingEnd : this.addDays(order, config.processingMin, true, config.closures);
      const dispatchEnd = personalized ? this.addDays(processingEnd, 7) : this.addDays(order, config.processingMax, true, config.closures);
      const arrival = !personalized && config.transitConfirmed === true && destination === 'FR-METRO';
      return {
        order,
        processingEnd,
        dispatchStart,
        dispatchEnd,
        arrivalStart: arrival ? this.addDays(dispatchStart, config.transitMin, true, config.closures) : null,
        arrivalEnd: arrival ? this.addDays(dispatchEnd, config.transitMax, true, config.closures) : null,
      };
    }

    connectedCallback() {
      try {
        this.settings = JSON.parse(this.querySelector('[data-delivery-config]').textContent);
      } catch (_) {
        return;
      }
      this.refresh = () => this.update();
      this.addEventListener('change', this.refresh);
      document.addEventListener('visibilitychange', this.refresh);
      window.addEventListener('pageshow', this.refresh);
      this.timer = window.setInterval(this.refresh, 60000);
      this.update();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.refresh);
      document.removeEventListener('visibilitychange', this.refresh);
      window.removeEventListener('pageshow', this.refresh);
      window.clearInterval(this.timer);
    }

    update(now = new Date()) {
      const destination = this.querySelector('[data-delivery-destination]');
      const result = ClairatierDeliveryEstimate.estimate(this.settings, now, destination ? destination.value : '');
      const dynamic = this.querySelector('[data-delivery-dates]');
      const fallback = this.querySelector('[data-delivery-fallback]');
      if (!result) {
        dynamic.hidden = true;
        fallback.hidden = false;
        if (destination) destination.closest('[data-delivery-choice]').hidden = true;
        return;
      }
      try {
        const formatter = new Intl.DateTimeFormat(this.settings.locale, { timeZone: 'UTC', day: 'numeric', month: 'long', year: 'numeric' });
        for (const [key, date] of Object.entries(result)) {
          const output = this.querySelector(`[data-delivery-date="${key}"]`);
          if (output && date) {
            output.textContent = formatter.format(date);
            output.dateTime = ClairatierDeliveryEstimate.iso(date);
          }
        }
      } catch (_) {
        dynamic.hidden = true;
        fallback.hidden = false;
        return;
      }
      dynamic.hidden = false;
      fallback.hidden = true;
      if (destination) destination.closest('[data-delivery-choice]').hidden = false;
      const arrival = this.querySelector('[data-delivery-arrival]');
      if (arrival) arrival.hidden = !result.arrivalStart;
      const destinationNote = this.querySelector('[data-delivery-destination-note]');
      if (destinationNote) destinationNote.hidden = Boolean(result.arrivalStart);
    }
  }

  customElements.define('clairatier-delivery-estimate', ClairatierDeliveryEstimate);
})();
